import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas } from "../GenericComponent";

import { hexToRGBA } from "../utils";

/**
 * Volume Profile Series
 *
 * Simplified implementation based on ChartIQ's approach.
 * Displays horizontal histogram showing volume distribution across price levels.
 *
 * Algorithm:
 * 1. Divide visible price range into N bins
 * 2. Accumulate volume for bars where price overlaps each bin
 * 3. Draw horizontal bars proportional to volume
 *
 * Reference: ChartIQ displayVolumeProfile (calcsChartIQ.js lines 3856-3992)
 */
class VolumeProfileSeries extends Component {
  constructor(props) {
    super(props);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
  }

  drawOnCanvas(ctx, moreProps) {
    const { bins, maxProfileWidthPercent, barsFill } = this.props;
    const {
      xScale,
      chartConfig: { yScale },
      plotData,
      width,
    } = moreProps;

    // Safety checks
    if (!plotData || plotData.length === 0) return;

    // Get visible price range from yScale
    const [lowValue, highValue] = yScale.domain();
    const priceRange = highValue - lowValue;

    if (priceRange <= 0) return;

    // Calculate price interval per bin
    const interval = priceRange / bins;

    // Initialize bins: [priceLevel, accumulatedVolume]
    const priceVolArray = [];
    for (let i = 0; i <= bins; i++) {
      const priceLevel = lowValue + i * interval;
      priceVolArray.push({
        priceLevel,
        volume: 0,
        x0: priceLevel,
        x1: Math.min(priceLevel + interval, highValue),
      });
    }

    // Ensure last bin covers exactly to highValue
    if (priceVolArray.length > 0) {
      priceVolArray[priceVolArray.length - 1].x1 = highValue;
    }

    if (priceVolArray.length < 2) return;

    // Accumulate volume in bins
    let volumeMax = 0;

    plotData.forEach((bar) => {
      if (!bar || bar.high == null || bar.low == null || bar.volume == null)
        return;

      // Volume can be either a number or an object { volume: number, direction: string }
      // Extract the numeric value
      const barVolume =
        typeof bar.volume === "object" ? bar.volume.volume : bar.volume;
      const barLow = bar.low;
      const barHigh = bar.high;

      // Check each bin to see if this bar's price overlaps it
      for (let i = 1; i < priceVolArray.length; i++) {
        const bin = priceVolArray[i];
        const bottomRange = priceVolArray[i - 1].priceLevel;
        const topRange = bin.priceLevel;

        // Check if bar overlaps this bin
        // Bar overlaps if: low is in range, or high is in range, or bar spans entire range
        if (
          (barLow >= bottomRange && barLow <= topRange) ||
          (barLow < bottomRange && barHigh > topRange) ||
          (barHigh >= bottomRange && barHigh <= topRange)
        ) {
          bin.volume += barVolume;
          if (bin.volume > volumeMax) volumeMax = bin.volume;
        }
      }
    });

    // No volume data
    if (volumeMax === 0) return;

    // Calculate bar dimensions
    const barMaxWidth = width * (maxProfileWidthPercent / 100);
    const barRight = width; // Bars extend from right edge leftward

    // Get color and opacity
    const color = barsFill?.color || "#b64a96";
    const opacity = barsFill?.opacity ?? 0.3;

    // Draw filled bars
    ctx.fillStyle = hexToRGBA(color, opacity);
    priceVolArray.slice(1).forEach((bin) => {
      if (bin.volume === 0) return;

      // Calculate bar width proportional to volume
      const barWidth = (bin.volume / volumeMax) * barMaxWidth;
      const barLeft = barRight - barWidth;

      // Get Y coordinates for this price bin
      const y1 = yScale(bin.x1);
      const y2 = yScale(bin.x0);
      const barHeight = y2 - y1;

      // Draw filled bar
      ctx.fillRect(barLeft, y1, barWidth, barHeight);
    });

    // Draw borders (ChartIQ style)
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    priceVolArray.slice(1).forEach((bin) => {
      if (bin.volume === 0) return;

      // Calculate bar width proportional to volume
      const barWidth = (bin.volume / volumeMax) * barMaxWidth;
      const barLeft = barRight - barWidth;

      // Get Y coordinates for this price bin
      const y1 = yScale(bin.x1);
      const y2 = yScale(bin.x0);
      const barHeight = y2 - y1;

      // Draw border
      ctx.strokeRect(barLeft, y1, barWidth, barHeight);
    });
  }

  render() {
    // Static rendering - no mouse interaction
    // Only redraws on pan/zoom, not on mousemove
    return (
      <GenericChartComponent
        canvasDraw={this.drawOnCanvas}
        canvasToDraw={getAxisCanvas}
        drawOn={["pan"]}
      />
    );
  }
}

VolumeProfileSeries.propTypes = {
  bins: PropTypes.number,
  maxProfileWidthPercent: PropTypes.number,
  barsFill: PropTypes.shape({
    color: PropTypes.string,
    opacity: PropTypes.number,
  }),
};

VolumeProfileSeries.defaultProps = {
  bins: 30,
  maxProfileWidthPercent: 25,
  barsFill: { color: "#b64a96", opacity: 0.3 }, // ChartIQ purple color
};

export default VolumeProfileSeries;
